<script lang="ts">
  // GameView1v1 — composicao da mesa 1v1. Layout:
  //
  //  [scoreboard topo]
  //  [oppPortrait + oppHand + vira]
  //  [plays no centro]
  //  [trucoBtn / actionPrompt]
  //  [my hand]
  //  [my portrait]
  //
  // Todos os filhos sao reativos via $matchSnapshot ou props derivadas.
  // TrucoModal e FinishedModal sao overlays globais.
  import type { MatchSnapshot, PlayerSnapshot } from '../../stores/match';
  import { displayedMatchSnapshot } from '../../stores/displayed';
  import { userId, avatar } from '../../stores/auth';
  import { resolveAvatar } from '../../lib/avatars';
  import Scoreboard from './Scoreboard.svelte';
  import PlayerPortrait from './PlayerPortrait.svelte';
  import Hand from './Hand.svelte';
  import OppHand from './OppHand.svelte';
  import Plays from './Plays.svelte';
  import Vira from './Vira.svelte';
  import ActionPrompt from './ActionPrompt.svelte';
  import TrucoButton from './TrucoButton.svelte';
  import TrucoActions from './TrucoActions.svelte';
  import FinishedModal from './FinishedModal.svelte';

  type Props = { snap: MatchSnapshot };
  let { snap }: Props = $props();

  // displaySnap segura a mesa por uns segundos quando a vaza fecha
  // (UX: user precisa ver a carta antes do snap "limpar"). Modais
  // continuam recebendo `snap` LIVE pra resposta imediata.
  let displaySnap = $derived($displayedMatchSnapshot ?? snap);

  // me/opp lookup pra 1v1 — checa displaySnap.p1/p2.
  let me = $derived.by((): PlayerSnapshot => {
    return displaySnap.p1?.id === $userId ? displaySnap.p1 : displaySnap.p2;
  });
  let opp = $derived.by((): PlayerSnapshot => {
    return displaySnap.p1?.id === $userId ? displaySnap.p2 : displaySnap.p1;
  });

  let isMyTurn = $derived(
    displaySnap.status === 'running' &&
    !displaySnap.truco_challenge_running &&
    displaySnap.next_to_play_id === $userId,
  );

  let pendingFromMe = $derived(
    displaySnap.truco_challenge_running && displaySnap.truco_challenger_id === $userId,
  );
  let pendingAgainstMe = $derived(
    displaySnap.truco_challenge_running &&
    !!displaySnap.truco_challenger_id &&
    displaySnap.truco_challenger_id !== $userId,
  );

  // Mode + texto do action-prompt em runtime.
  type PromptMode = 'mine' | 'theirs' | 'truco-me' | 'truco-opp' | 'finished-win' | 'finished-lose' | 'idle';
  let promptMode = $derived.by((): PromptMode => {
    if (displaySnap.status === 'finished') {
      if (!displaySnap.winner) return 'idle';
      return displaySnap.winner === $userId ? 'finished-win' : 'finished-lose';
    }
    if (pendingFromMe) return 'truco-me';
    if (pendingAgainstMe) return 'truco-opp';
    if (isMyTurn) return 'mine';
    return 'theirs';
  });
  let promptText = $derived.by(() => {
    switch (promptMode) {
      case 'mine': return 'sua vez — escolha uma carta';
      case 'theirs': return 'aguardando oponente';
      case 'truco-me': return 'aguardando resposta';
      case 'truco-opp': return 'TRUCO! responda';
      case 'finished-win': return 'vitória!';
      case 'finished-lose': return 'fim de partida';
      default: return '—';
    }
  });
</script>

<section class="game-1v1">
  <div class="head">
    <Scoreboard snap={displaySnap} {me} {opp} vira={displaySnap.manilha} />
  </div>

  <div class="opp-row">
    <PlayerPortrait
      name={opp.name}
      side="opp"
      active={displaySnap.next_to_play_id === opp.id}
      avatarKey={resolveAvatar(opp.avatar, opp.id, $avatar, true)}
      deadline={displaySnap.next_to_play_id === opp.id ? displaySnap.deadline : null}
      turnDurationSec={displaySnap.turn_duration_sec ?? 30}
      playerId={opp.id}
      ranking={opp.ranking_score}
    />
    <OppHand cards={opp.cards} roundNumber={displaySnap.round_number ?? 0} />
    <Vira vira={displaySnap.manilha} />
  </div>

  <div class="mesa">
    <Plays {me} {opp} vira={displaySnap.manilha} />
  </div>

  <!-- Action row usa LIVE snap. Quando o oponente pediu truco e cabe
       a mim responder, TrucoActions toma TODA a linha (esconde
       prompt + truco-btn) — botoes ja sao o prompt. -->
  {#if snap.truco_challenge_running && snap.truco_challenger_id && snap.truco_challenger_id !== $userId}
    <div class="action-row">
      <TrucoActions {snap} />
    </div>
  {:else}
    <div class="action-row">
      <ActionPrompt
        mode={promptMode}
        text={promptText}
        deadline={pendingFromMe ? snap.deadline : null}
      />
      <TrucoButton {snap} />
    </div>
  {/if}

  <div class="my-row">
    <Hand cards={me.cards} vira={displaySnap.manilha} canPlay={isMyTurn} snap={snap} />
  </div>

  <div class="me-portrait">
    <PlayerPortrait
      name={me.name}
      side="me"
      active={isMyTurn}
      avatarKey={resolveAvatar(me.avatar, me.id, $avatar, false)}
      deadline={isMyTurn ? displaySnap.deadline : null}
      turnDurationSec={displaySnap.turn_duration_sec ?? 30}
      playerId={me.id}
      ranking={me.ranking_score}
    />
  </div>
</section>

<!-- Modal usa LIVE snap — quer resposta imediata, sem hold. -->
<FinishedModal {snap} />

<style>
  /* Game 1v1 ocupa altura disponivel (main e flex column).
   * Plays no centro tem flex:1 — absorve sobra. Cards usam
   * var responsiva — escala sem achatar nem estourar. */
  .game-1v1 {
    width: 100%;
    max-width: 480px;
    height: 100%;
    min-height: 0;
    margin: 0 auto;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: clamp(6px, 1.5vh, 12px);
    padding: 0 10px;
    container-type: size;
  }
  .game-1v1 {
    --card-w-sm: clamp(28px, max(7cqw, 4cqh), 46px);
    --card-w-md: clamp(34px, max(9cqw, 5.5cqh), 58px);
    --card-w-lg: clamp(40px, max(11cqw, 6.5cqh), 70px);
  }

  .head { display: flex; justify-content: center; flex: 0 0 auto; }

  .opp-row {
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    flex: 0 0 auto;
  }

  /* Mesa absorve TODO o espaco extra entre opp e action rows.
   * min-height: 0 deixa o conteudo encolher se necessario. */
  .mesa {
    width: 100%;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background:
      radial-gradient(ellipse at center, #25744a 0%, #103820 60%, #051c0d 100%);
    border: 2px solid #4a2c10;
    border-radius: clamp(20px, 4vw, 40px) / clamp(16px, 3vw, 28px);
    padding: clamp(10px, 2vh, 18px);
    perspective: 1200px;
    transform: rotateX(6deg);
    transform-origin: center top;
    box-shadow:
      inset 0 0 80px rgba(0, 0, 0, 0.55),
      inset 0 2px 4px rgba(255, 255, 255, 0.08),
      inset 0 -20px 40px rgba(0, 0, 0, 0.35),
      0 24px 40px -12px rgba(0, 0, 0, 0.7),
      0 10px 20px rgba(0, 0, 0, 0.5);
  }
  .mesa :global(.plays) {
    min-height: 0 !important;
    padding: 8px 6px !important;
  }

  /* Grid 1fr/auto: o prompt fica na coluna esquerda (cresce/encolhe com
     o timer dentro do próprio espaço), o TRUCO! fica fixo na coluna
     direita. Antes era flex space-between, então o prompt mudando largura
     a cada segundo do countdown empurrava o botão lateralmente. */
  .action-row {
    display: grid;
    grid-template-columns: 1fr auto;
    align-items: center;
    gap: 10px;
    width: 100%;
    flex: 0 0 auto;
  }
  .my-row { width: 100%; flex: 0 0 auto; }
  .me-portrait { display: flex; justify-content: center; flex: 0 0 auto; }

  @media (max-width: 480px) {
    .game-1v1 { gap: clamp(4px, 1vh, 8px); padding: 0 6px; }
  }
</style>
